/**
 * Carousel Text Module
 *
 * Converts a WordPress list block with the class `carousel-text` into a
 * looping Swiper carousel at runtime. The original markup needs no changes
 * beyond adding the `carousel-text` class — this module wraps the `<li>`
 * items in the Swiper-required structure automatically.
 *
 * Expected markup:
 *   <ul class="wp-block-list carousel-text">
 *     <li>Item 1</li>
 *     <li>Item 2</li>
 *   </ul>
 *
 * @package EVT Restaurant Builder
 * @since 1.0.0
 */

/**
 * Initialise all `.carousel-text` list instances on the page.
 */
export function initCarouselText() {
	const lists = document.querySelectorAll( 'ul.carousel-text' );

	if ( ! lists.length ) {
		return;
	}

	if ( typeof Swiper === 'undefined' ) {
		console.warn( 'Swiper library not loaded. Carousel text will display as a static list.' );
		return;
	}

	lists.forEach( ( list ) => {
		const items = Array.from( list.querySelectorAll( ':scope > li' ) );

		if ( ! items.length ) {
			return;
		}

		// Build the swiper-wrapper and move each <li> into a swiper-slide.
		const wrapper = document.createElement( 'div' );
		wrapper.classList.add( 'swiper-wrapper' );

		items.forEach( ( item ) => {
			const slide = document.createElement( 'div' );
			slide.classList.add( 'swiper-slide' );
			slide.appendChild( item );
			wrapper.appendChild( slide );
		} );

		// Build navigation buttons.
		const btnPrev = document.createElement( 'div' );
		btnPrev.classList.add( 'swiper-button-prev' );

		const btnNext = document.createElement( 'div' );
		btnNext.classList.add( 'swiper-button-next' );

		// Replace list contents with Swiper structure.
		list.innerHTML = '';
		list.classList.add( 'swiper' );
		list.appendChild( wrapper );
		list.appendChild( btnPrev );
		list.appendChild( btnNext );

		const slideCount = items.length;

		// With slidesPerView:'auto' Swiper cannot calculate how many clones it
		// needs, so loop silently fails unless we pre-duplicate slides.
		// Ensure the wrapper holds at least 3× the original slides.
		const minSlides = slideCount * 3;
		if ( wrapper.children.length < minSlides ) {
			const baseSlides = Array.from( wrapper.children );
			let i = 0;
			while ( wrapper.children.length < minSlides ) {
				const clone = baseSlides[ i % baseSlides.length ].cloneNode( true );
				clone.setAttribute( 'aria-hidden', 'true' );
				wrapper.appendChild( clone );
				i++;
			}
		}

		new Swiper( list, {
			slidesPerView: 'auto',
			spaceBetween: 24,
			loop: true,
			speed: 4000,
			freeMode: {
				enabled: true,
				momentum: false,
			},

			autoplay: {
				delay: 0,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			},

			navigation: {
				prevEl: btnPrev,
				nextEl: btnNext,
			},

			a11y: {
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
			},
		} );
	} );
}
